import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import StudentGapTask from 'Components/ChecklistTasks/StudentGapTask';
import StudentMathFormulasTask from 'Components/ChecklistTasks/StudentMathFormulasTask';
import StudentTestTask from 'Components/ChecklistTasks/StudentTestTask';
import StudentUnderlineTask from 'Components/ChecklistTasks/StudentUnderlineTask';
import StudentWordCompositionTask from 'Components/ChecklistTasks/StudentWordCompositionTask';
import StudentWordStressTask from 'Components/ChecklistTasks/StudentWordStressTask';
import { iclApi, taskApi } from '../../api';
import { getStudents } from '../../Common/Database';
import StudentExcludesTask from '../../Components/ChecklistTasks/StudentExludesTask';
import StudentSelectOneTask from '../../Components/ChecklistTasks/StudentSelectOneTask';
import StudentSentenceStructureTask from '../../Components/ChecklistTasks/StudentSentenceStructure';
import StudentTaskWithLink from '../../Components/ChecklistTasks/StudentTaskWithLink';
import StudentTextTask from '../../Components/ChecklistTasks/StudentTextTask';
import EditorCanvas from '../../Components/ImageEditor/EditorCanvas';
import EditorProvider from '../../Components/ImageEditor/EditorProvider';
import { LoadingError } from '../../Components/LoadingError';
import { Exam, TaskKeysType, WorkType } from '../../shared/types';
import { SummarizeTask } from '../../shared/types/task';
import { ref, update } from 'firebase/database';
import { database } from 'Common/Firebase';
import { format } from 'date-fns';
import moment from 'moment/moment';
import { v4 as uuidv4 } from 'uuid';
import './index.scss';

interface Props {
  mail: string;
  preview?: boolean;
  user: any;
}

interface StateRoute {
  examId: string | null;
  taskId: string | null;
}

const componentMapping = {
  text: StudentTextTask,
  link: StudentTaskWithLink,
  test: StudentTestTask,
  exclude: StudentExcludesTask,
  selectOne: StudentSelectOneTask,
  sentenceStructure: StudentSentenceStructureTask,
  gap: StudentGapTask,
  mathFormulas: StudentMathFormulasTask,
  wordComposition: StudentWordCompositionTask,
  wordStress: StudentWordStressTask,
  underline: StudentUnderlineTask,
};

export default function ExamTeacherNew({ mail, preview, user }: Props) {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();

  const location = useLocation();
  const routeState = location.state as StateRoute | null;
  const examId = routeState?.examId as StateRoute['examId'];
  const [iclData, setIclData] = useState<Exam | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [tasks, setTasks] = useState<SummarizeTask[]>([]);
  const [studentName, setStudentName] = useState<string>('');

  const totalTeacherPoints = tasks.reduce((total, task) => total + (task.teacherPoints ?? 0), 0);

  useEffect(() => {
    fetchIcls();
  }, []);

  const fetchIcls = async () => {
    if (examId) {
      setLoading(true);

      await iclApi.getICL(examId).then(data => {
        setIclData(data);

        setLoading(false);
      });
    }
  };

  useEffect(() => {
    if (iclData) {
      getStudents().then(students => {
        const student = students.find(s => s.email?.replace(/\./g, '') === iclData.studentEmail);
        if (student) {
          setStudentName(student.FIO);
        }
      });
    }
  }, [iclData]);

  useEffect(() => {
    if (examId) {
      const updateTasks = (tasks: SummarizeTask[]) => setTasks(tasks);
      const unsubscribe = taskApi.subscribeToICLTasks({ ICLId: examId, setNewTasks: updateTasks });

      return () => {
        unsubscribe();
      };
    }
  }, []);

  const handleChangeTaskByKey = (id: string, key: TaskKeysType, value: string[] | string | boolean | number) => {
    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index], [key]: value };

    newTaskArray.splice(index, 1, updatedObject);

    if (examId) {
      if (updatedObject.checkedAt || !updatedObject.checkedAt) {
        taskApi.updateTask<SummarizeTask>(examId, updatedObject);
      }
    }

    setTasks(newTaskArray);
  };

  const saveTaskAnswer = async (id: string) => {
    setLoading(true);

    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index] };

    newTaskArray.splice(index, 1, updatedObject);

    if (examId) {
      await taskApi.updateTask<SummarizeTask>(examId, updatedObject).then(() => setLoading(false));
    }
  };

  const renderTasks = () => {
    const activeTasks = tasks.filter(it => it.isActive);

    return activeTasks.map((it: SummarizeTask, index: number) => {
      const Component = componentMapping[it.type];

      return (
        <Component
          icl={iclData}
          index={index}
          key={it.id}
          {...it}
          handleChangeTaskByKey={handleChangeTaskByKey}
          saveTaskAnswer={saveTaskAnswer}
          preview={preview}
          workTaskType={WorkType.exam}
          teacherPoints={it.teacherPoints}
        />
      );
    });
  };

  const sendCompleteExamNotification = async () => {
    if (!iclData) {
      return;
    }

    const studentEmail = iclData?.studentEmail;
    const now = new Date();
    const formattedTime = format(now, 'dd MM yyyy HH:mm');
    const notificationId = uuidv4();

    const notificationData = {
      id: notificationId + studentEmail,
      idItem: studentEmail,
      subject: iclData?.subject,
      name: user?.FIO,
      time: formattedTime,
      type: 'examChecked',
      iclId: iclData?.id,
      workType: iclData?.type,
      viewed: true,
    };

    try {
      await update(ref(database, `Students/${studentEmail}/notification`), {
        [notificationId + studentEmail]: notificationData,
      });

      const newIcl = {
        ...iclData,
        status: 'checked',
        time: formattedTime,
        totalPoint: totalTeacherPoints,
      };

      iclApi.updateICL(newIcl);

      fetchIcls();
    } catch (error) {
      console.log(error, 'error sending notification');
    }
  };

  const status = iclData?.type === 'exam' && iclData.status ? iclData?.status : null;

  return (
    <EditorProvider>
      <LoadingError isLoading={isLoading && !tasks}>
        <div className="mx-auto rounded-[24px] border-x-[8px] border-b-[8px] border-[#9CDFFC7F] border-opacity-50 bg-white px-3 py-4 shadow-none lg:w-11/12 lg:!px-[120px] lg:!py-[40px]">
          <div className="flex w-fit flex-col gap-[12px] lg:w-full lg:gap-[20px]">
            <div className="lg:leading-12 text-[24px] font-bold leading-9 lg:text-[48px]">
              {t('examlist')} №<span>{iclData?.number}</span>
            </div>

            <div className="text-[20px] font-bold leading-7 lg:text-[32px] lg:leading-10">
              {t('Cource name')}: <span className="font-medium">{iclData?.courseName}</span>
            </div>

            <div className="text-[18px] font-bold leading-6 lg:text-[24px] lg:leading-8">
              {t('theme')}: <span className="font-medium">{iclData?.theme}</span>
            </div>

            <div className="flex flex-col text-[18px] font-bold leading-6 lg:leading-8">
              {t('Purpose of the course')}: <span className="font-medium">{iclData?.courseGoal}</span>
            </div>

            <div className="flex flex-col gap-[12px] md:flex-row md:gap-[30%] lg:flex-row lg:gap-[30%]">
              <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                {t('subject')}:{' '}
                <span className="font-medium">
                  {' '}
                  {platformLang === 'ua' ? iclData?.subject.name : iclData?.subject.ruName}
                </span>
              </div>

              <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                {t('language')}: <span className="font-medium">{iclData?.language}</span>
              </div>

              <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                {t('class')}: <span className="font-medium">{t(iclData?.class)}</span>
              </div>
            </div>

            {studentName && (
              <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                {t('Student')}: <span className="font-medium">{studentName}</span>
              </div>
            )}

            {status && (
              <div className="flex flex-col text-[18px] font-bold leading-6 lg:leading-8">
                {`${t('status')}: ${t(`exam status ${status}`)}`}
              </div>
            )}
          </div>

          <EditorCanvas icl={iclData} handleChangeTaskByKey={handleChangeTaskByKey} />

          <div>{renderTasks()}</div>

          {iclData?.status !== 'checked' && (
            <div className="final-exam">
              <p className="warning-text mb-0 text-center text-[20px] font-medium leading-[20px] text-[#6B7280]">
                {t('completeExamText')}
              </p>

              <button
                type="button"
                className="btn more h-[43px] w-full bg-[#DCF0FF] !text-[16px] lg:w-[240px] lg:!text-[18px]"
                // disabled={!allCompleted}

                onClick={() => sendCompleteExamNotification()}
              >
                {t('completeExam')}
              </button>
            </div>
          )}
        </div>
      </LoadingError>
    </EditorProvider>
  );
}
